import {
  AvatarTitleCell,
  IconAndTextCell,
  Badge,
  Stack,
  Typography,
  colors,
  BadgeColorVariant,
  Tooltip,
} from '@ui-components/neo.tax';
import { useMemo } from 'react';
import { CellProps, Column } from 'react-table';

import {
  OrganizationInvitationDerivedState,
  OrganizationMemberItemScope,
  OrganizationMembersTableItem,
  OrganizationMemberTableItemMember,
} from '@neo.tax/shared/types';
import { companyMemberItemScopeDisplayName, isInvitation } from '@neo.tax/shared/utils';

import { StyledEmailGeneralIcon } from '../organization-members.styled';

const scopeToBadgeVariant: Record<OrganizationMemberItemScope, BadgeColorVariant> = {
  [OrganizationMemberItemScope.ADMIN]: 'green',
  [OrganizationMemberItemScope.MEMBER]: 'yellow',
  [OrganizationMemberItemScope.NON_FINANCIAL]: 'blue',
};

export const useOrganizationMemberColumns = ({
  setIsMemberDrawerOpen,
  setMemberId,
}: {
  setMemberId: (id: string) => void;
  setIsMemberDrawerOpen: (isOpen: boolean) => void;
}): Array<Column<OrganizationMembersTableItem>> =>
  useMemo(
    () => [
      {
        id: 'memberName',
        Header: 'Member Name',
        width: 'minmax(150px, 435px)',
        accessor: (item) => item,
        Cell: ({
          value,
        }: React.PropsWithChildren<
          CellProps<OrganizationMembersTableItem, OrganizationMembersTableItem>
        >): JSX.Element => {
          if (isInvitation(value)) {
            // Parent OrganizationInvitation rows carry derivedState; child rows inherit from
            // the parent and fall through to the default "Invited" badge. The pre-lifecycle
            // "Invite Failed" badge is gone — the equivalent is EXPIRED/REVOKED now.
            const derivedState: OrganizationInvitationDerivedState = value.derivedState ?? 'PENDING';
            const colorVariant: BadgeColorVariant =
              derivedState === 'PENDING' ? 'yellow' : derivedState === 'ACCEPTED' ? 'green' : 'red';
            const badgeText =
              derivedState === 'PENDING'
                ? 'Invited'
                : derivedState === 'EXPIRED'
                  ? 'Expired'
                  : derivedState === 'REVOKED'
                    ? 'Revoked'
                    : 'Accepted';
            return (
              <Stack direction="row" space={2} overflow="hidden">
                {/* `min-w-0` lets a long email shrink and truncate instead of pushing the badge out of the cell. */}
                <Tooltip text={value.email} anchorClassName="block min-w-0 overflow-hidden">
                  <AvatarTitleCell title={value.email} size={6} fontSize="sm" fontWeight="normal" />
                </Tooltip>
                <Badge className="shrink-0" colorVariant={colorVariant}>
                  {badgeText}
                </Badge>
              </Stack>
            );
          }
          return (
            <Stack direction="row" space={2} overflow="hidden">
              <AvatarTitleCell title={value.fullName} size={6} fontSize="sm" fontWeight="normal" />
            </Stack>
          );
        },
      },
      {
        id: 'companyCount',
        Header: 'Companies',
        width: 136,
        accessor: (item) => item,
        Cell: ({
          value,
        }: React.PropsWithChildren<
          CellProps<OrganizationMembersTableItem, OrganizationMembersTableItem>
        >): JSX.Element => {
          const companyNames = value.companies.map((company) => company.shortName).join(', ');
          return (
            <Stack fullWidth justifyContent="start">
              <Typography title={companyNames}>{value.companies.length}</Typography>
            </Stack>
          );
        },
      },
      {
        id: 'email',
        Header: 'Email Address',
        accessor: 'email',
        width: 350,
        Cell: ({ value }: CellProps<OrganizationMemberTableItemMember, string>): JSX.Element => (
          <IconAndTextCell icon={<StyledEmailGeneralIcon />} text={value} />
        ),
      },
      {
        id: 'scope',
        Header: 'Role',
        width: 'minmax(150px, 2fr)',
        accessor: (value) => value.scope,
        Cell: ({
          value,
        }: React.PropsWithChildren<
          CellProps<OrganizationMembersTableItem, OrganizationMemberItemScope>
        >): JSX.Element => (
          <Stack>
            <Badge colorVariant={scopeToBadgeVariant[value]}>{companyMemberItemScopeDisplayName[value]}</Badge>
          </Stack>
        ),
      },
      {
        id: 'delete',
        width: 130,
        accessor: (datum: OrganizationMembersTableItem): OrganizationMembersTableItem => datum,
        Cell: ({
          value,
        }: React.PropsWithChildren<
          CellProps<OrganizationMembersTableItem, OrganizationMembersTableItem>
        >): JSX.Element => (
          <Stack fullWidth justifyContent="end">
            <Typography
              as="button"
              color={colors.primary[600]}
              onClick={(): void => {
                setIsMemberDrawerOpen(true);
                setMemberId(value.id);
              }}
              fontSize="sm"
              fontWeight="medium"
            >
              View/Edit
            </Typography>
          </Stack>
        ),
      },
    ],
    [setIsMemberDrawerOpen, setMemberId],
  );
